import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Alert,
    FormControlLabel,
    Checkbox,
    Paper,
} from '@mui/material';
import { ContentCopy, CheckCircleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * One-time secret display. The secret is held in component state only —
 * never persisted to a global store or localStorage. Once dismissed
 * it's gone for good and the admin must rotate to get a new one.
 */
const WebhookSecretDialog = ({ open, secret, onClose }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [acknowledged, setAcknowledged] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!secret) return;
        try {
            await navigator.clipboard.writeText(secret);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            // ignore — browser without clipboard support
        }
    };

    const handleClose = () => {
        if (!acknowledged) return;
        setAcknowledged(false);
        setCopied(false);
        onClose?.();
    };

    return (
        <Dialog
            open={open}
            // Force the admin to acknowledge — no backdrop/escape close.
            disableEscapeKeyDown
            onClose={(_e, reason) => {
                if (reason === 'backdropClick') return;
                handleClose();
            }}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? 'احفظ هذا السر الآن' : 'Save this secret now'}
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                        {isArabic
                            ? 'لن تتمكن من رؤية هذا السر مرة أخرى. انسخه واحفظه في مكان آمن قبل الإغلاق.'
                            : "You won't be able to see this secret again. Copy it and store it somewhere safe before closing."}
                    </Typography>
                </Alert>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    {isArabic ? 'السر' : 'Secret'}
                </Typography>

                <Paper
                    variant="outlined"
                    sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'grey.50'),
                    }}
                >
                    <Box
                        component="code"
                        sx={{
                            flex: 1,
                            fontFamily: 'monospace',
                            fontSize: 13,
                            wordBreak: 'break-all',
                            userSelect: 'all',
                        }}
                    >
                        {secret}
                    </Box>
                    <Tooltip title={copied ? (isArabic ? 'تم النسخ' : 'Copied') : (isArabic ? 'نسخ' : 'Copy')}>
                        <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
                            {copied ? <CheckCircleOutline fontSize="small" /> : <ContentCopy fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                </Paper>

                <FormControlLabel
                    sx={{ mt: 2, alignItems: 'flex-start' }}
                    control={
                        <Checkbox
                            checked={acknowledged}
                            onChange={(e) => setAcknowledged(e.target.checked)}
                        />
                    }
                    label={
                        <Typography variant="body2">
                            {isArabic
                                ? 'لقد حفظت هذا السر في مكان آمن.'
                                : "I've saved this secret somewhere safe."}
                        </Typography>
                    }
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    variant="contained"
                    disabled={!acknowledged}
                    onClick={handleClose}
                >
                    {isArabic ? 'تم' : 'Done'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WebhookSecretDialog;
